/**
 * SmartElementDetector - Auto-detection of Text Containers and Layout Elements
 * 
 * This system scans React component children to automatically identify:
 * - Text elements that need intelligent rendering
 * - Container bounds and spatial relationships
 * - Content priority and context types
 * 
 * CRITICAL: This is read-only analysis - NO rendering changes
 */

import React from 'react';
import type {
  SmartElement,
  Rectangle,
  ElementType,
  ContextType,
  ContentPriority,
  RenderMode
} from './SmartLayout.types';

/**
 * Detection result for a single element
 */
interface DetectedElement {
  element: React.ReactElement | string;
  bounds: Rectangle;
  text: string;
  confidence: number; // 0-1: How confident we are about this detection
  suggestedType: ElementType;
  suggestedContext: ContextType;
  suggestedPriority: ContentPriority;
  metadata: {
    tagName?: string;
    className?: string;
    id?: string;
    fontSize?: number;
    isTitle?: boolean;
    isSubtitle?: boolean;
    isButton?: boolean;
    isLabel?: boolean;
  };
}

/**
 * Configuration for auto-detection behavior
 */
interface DetectionConfig {
  scanDepth: number;           // How deep to traverse component tree
  minTextLength: number;       // Ignore text shorter than this
  minContainerSize: number;    // Ignore containers smaller than this
  includeHiddenElements: boolean;
  confidenceThreshold: number; // Only return elements above this confidence
}

export class SmartElementDetector {
  private static readonly DEFAULT_CONFIG: DetectionConfig = {
    scanDepth: 5,
    minTextLength: 2,
    minContainerSize: 20,
    includeHiddenElements: false,
    confidenceThreshold: 0.5
  };

  /**
   * Main detection method - scans React children for text containers
   * SAFE: Read-only analysis, no side effects
   */
  static detectElements(
    children: React.ReactNode,
    containerBounds: Rectangle,
    config: Partial<DetectionConfig> = {}
  ): SmartElement[] {
    const fullConfig = { ...this.DEFAULT_CONFIG, ...config };
    const detected = this.scanChildren(children, containerBounds, 0, fullConfig);
    
    // Convert detected elements to SmartElements
    return detected
      .filter(d => d.confidence >= fullConfig.confidenceThreshold)
      .map(d => this.convertToSmartElement(d));
  }

  /**
   * Recursively scan React children
   */
  private static scanChildren(
    children: React.ReactNode,
    parentBounds: Rectangle,
    depth: number,
    config: DetectionConfig
  ): DetectedElement[] {
    if (depth > config.scanDepth) return [];

    const detected: DetectedElement[] = [];

    React.Children.forEach(children, (child) => {
      if (typeof child === 'string') {
        // Direct text content
        if (child.trim().length >= config.minTextLength) {
          detected.push(this.createTextElement(child, parentBounds));
        }
      } else if (React.isValidElement(child)) {
        // React element - analyze it
        const elementDetection = this.analyzeElement(child, parentBounds, depth, config);
        if (elementDetection) {
          detected.push(elementDetection);
        }

        // Recursively scan children
        if (child.props.children) {
          const childBounds = this.estimateChildBounds(child, parentBounds);
          const childDetections = this.scanChildren(
            child.props.children,
            childBounds,
            depth + 1,
            config
          );
          detected.push(...childDetections);
        }
      }
    });

    return detected;
  }

  /**
   * Analyze a React element to determine if it's a text container
   */
  private static analyzeElement(
    element: React.ReactElement,
    parentBounds: Rectangle,
    depth: number,
    config: DetectionConfig
  ): DetectedElement | null {
    const elementType = this.getElementType(element);
    const bounds = this.estimateElementBounds(element, parentBounds);
    
    // Skip if too small
    if (bounds.width < config.minContainerSize || bounds.height < config.minContainerSize) {
      return null;
    }

    const text = this.extractTextContent(element);
    if (!text || text.length < config.minTextLength) {
      return null;
    }

    const metadata = this.analyzeElementMetadata(element);
    const confidence = this.calculateConfidence(element, text, metadata);
    
    if (confidence < config.confidenceThreshold) {
      return null;
    }

    return {
      element,
      bounds,
      text,
      confidence,
      suggestedType: this.suggestElementType(metadata),
      suggestedContext: this.suggestContextType(metadata),
      suggestedPriority: this.suggestPriority(metadata),
      metadata
    };
  }

  /**
   * Extract text content from React element recursively
   */
  private static extractTextContent(element: React.ReactElement): string {
    if (typeof element.props.children === 'string') {
      return element.props.children.trim();
    }

    if (Array.isArray(element.props.children)) {
      return element.props.children
        .map((child: React.ReactNode) => {
          if (typeof child === 'string') return child;
          if (React.isValidElement(child)) return this.extractTextContent(child);
          return '';
        })
        .join(' ')
        .trim();
    }

    if (React.isValidElement(element.props.children)) {
      return this.extractTextContent(element.props.children);
    }

    return '';
  }

  /**
   * Get the element type (tag name or component name)
   */
  private static getElementType(element: React.ReactElement): string {
    if (typeof element.type === 'string') {
      return element.type; // HTML tag like 'text', 'tspan', 'div'
    }
    if (typeof element.type === 'function') {
      return element.type.name || 'Component'; // React component
    }
    return 'Unknown';
  }

  /**
   * Estimate element bounds based on props and context
   */
  private static estimateElementBounds(
    element: React.ReactElement,
    parentBounds: Rectangle
  ): Rectangle {
    const props = element.props;
    
    // SVG elements with explicit positioning
    if (props.x !== undefined && props.y !== undefined) {
      return {
        x: Number(props.x) || 0,
        y: Number(props.y) || 0,
        width: Number(props.width) || parentBounds.width,
        height: Number(props.height) || parentBounds.height
      };
    }

    // For now, estimate based on parent bounds
    // Phase 3 will implement more sophisticated bounds calculation
    return {
      x: parentBounds.x,
      y: parentBounds.y,
      width: parentBounds.width * 0.8, // Assume some padding
      height: parentBounds.height * 0.2  // Estimate height
    };
  }

  /**
   * Estimate bounds for child elements
   */
  private static estimateChildBounds(
    element: React.ReactElement,
    parentBounds: Rectangle
  ): Rectangle {
    // For now, return parent bounds
    // Phase 3 will implement layout-aware bounds calculation
    return parentBounds;
  }

  /**
   * Analyze element metadata for classification
   */
  private static analyzeElementMetadata(element: React.ReactElement) {
    const props = element.props;
    const elementType = this.getElementType(element);
    
    return {
      tagName: elementType,
      className: props.className || props.class,
      id: props.id,
      fontSize: props.fontSize || props['font-size'],
      isTitle: this.isTitle(elementType, props),
      isSubtitle: this.isSubtitle(elementType, props),
      isButton: this.isButton(elementType, props),
      isLabel: this.isLabel(elementType, props)
    };
  }

  /**
   * Classification helpers
   */
  private static isTitle(elementType: string, props: any): boolean {
    return (
      elementType.toLowerCase().includes('title') ||
      elementType.toLowerCase().includes('header') ||
      /h[1-6]/i.test(elementType) ||
      props.className?.includes('title') ||
      props.className?.includes('header') ||
      props.className?.includes('heading')
    );
  }

  private static isSubtitle(elementType: string, props: any): boolean {
    return (
      elementType.toLowerCase().includes('subtitle') ||
      props.className?.includes('subtitle') ||
      props.className?.includes('subheading')
    );
  }

  private static isButton(elementType: string, props: any): boolean {
    return (
      elementType.toLowerCase() === 'button' ||
      props.className?.includes('button') ||
      props.className?.includes('btn') ||
      props.role === 'button'
    );
  }

  private static isLabel(elementType: string, props: any): boolean {
    return (
      elementType.toLowerCase() === 'label' ||
      props.className?.includes('label')
    );
  }

  /**
   * Calculate confidence score for detection
   */
  private static calculateConfidence(
    element: React.ReactElement,
    text: string,
    metadata: any
  ): number {
    let confidence = 0.3; // Base confidence

    // Text quality indicators
    if (text.length > 10) confidence += 0.2;
    if (text.length > 30) confidence += 0.1;
    
    // Element type indicators
    if (metadata.isTitle) confidence += 0.3;
    if (metadata.isSubtitle) confidence += 0.25;
    if (metadata.isButton) confidence += 0.2;
    if (metadata.isLabel) confidence += 0.15;

    // Explicit positioning (SVG elements)
    if (element.props.x !== undefined || element.props.y !== undefined) {
      confidence += 0.2;
    }

    return Math.min(confidence, 1.0);
  }

  /**
   * Suggest element type based on analysis
   */
  private static suggestElementType(metadata: any): ElementType {
    if (metadata.isButton) return 'text-container';
    if (metadata.isTitle || metadata.isSubtitle) return 'text-container';
    if (metadata.isLabel) return 'label-target';
    
    return 'text-container'; // Default
  }

  /**
   * Suggest context type based on analysis
   */
  private static suggestContextType(metadata: any): ContextType {
    if (metadata.isButton) return 'button';
    if (metadata.isTitle) return 'header';
    if (metadata.isSubtitle) return 'header';
    if (metadata.isLabel) return 'label';
    
    return 'body'; // Default
  }

  /**
   * Suggest content priority based on analysis
   */
  private static suggestPriority(metadata: any): ContentPriority {
    if (metadata.isTitle) return 'critical';
    if (metadata.isButton) return 'critical';
    if (metadata.isSubtitle) return 'important';
    
    return 'optional'; // Default for body text
  }

  /**
   * Convert detected element to SmartElement
   */
  private static convertToSmartElement(detected: DetectedElement): SmartElement {
    return {
      id: detected.metadata.id || `auto-detected-${Math.random().toString(36).substr(2, 9)}`,
      type: detected.suggestedType,
      bounds: detected.bounds,
      content: {
        text: detected.text,
        alternatives: this.generateTextAlternatives(detected.text),
        priority: detected.suggestedPriority,
        contextType: detected.suggestedContext
      },
      constraints: {
        minFontSize: Math.max(8, (detected.metadata.fontSize || 12) * 0.7),
        maxLines: this.getMaxLines(detected.suggestedContext),
        padding: { top: 5, right: 5, bottom: 5, left: 5 },
        wordWrap: true
      },
      renderModes: this.getSuggestedRenderModes(detected.suggestedContext, detected.suggestedPriority)
    };
  }

  /**
   * Generate text alternatives for different container sizes
   */
  private static generateTextAlternatives(text: string): string[] {
    const alternatives = [text]; // Always include original

    // Generate shorter versions
    const words = text.split(' ');
    if (words.length > 3) {
      alternatives.push(words.slice(0, Math.ceil(words.length / 2)).join(' ') + '...');
    }
    if (words.length > 1) {
      alternatives.push(words[0] + '...');
    }

    // First letter for extreme cases
    if (text.length > 0) {
      alternatives.push(text.charAt(0).toUpperCase());
    }

    return alternatives;
  }

  /**
   * Get maximum lines based on context
   */
  private static getMaxLines(context: ContextType): number {
    switch (context) {
      case 'button': return 2;
      case 'header': return 3;
      case 'label': return 1;
      case 'caption': return 2;
      case 'badge': return 1;
      default: return 4;
    }
  }

  /**
   * Get suggested render modes based on context and priority
   */
  private static getSuggestedRenderModes(
    context: ContextType,
    priority: ContentPriority
  ): RenderMode[] {
    if (priority === 'critical') {
      return ['full-text', 'abbreviated', 'external-label'];
    }
    
    if (context === 'button') {
      return ['full-text', 'abbreviated', 'icon-only'];
    }
    
    if (context === 'header') {
      return ['full-text', 'abbreviated', 'external-label'];
    }
    
    return ['full-text', 'abbreviated', 'conceptual-bar', 'hidden'];
  }

  /**
   * Create text element for direct string content
   */
  private static createTextElement(text: string, bounds: Rectangle): DetectedElement {
    return {
      element: text,
      bounds,
      text: text.trim(),
      confidence: 0.6, // Medium confidence for direct text
      suggestedType: 'text-container',
      suggestedContext: 'body',
      suggestedPriority: 'optional',
      metadata: {
        tagName: 'text'
      }
    };
  }
}